/**
 * Country Switcher Popup
 * Handles opening/closing popup, dropdown interactions, search, and form submission
 */

class CountrySwitcherPopup {
  constructor() {
    this.popup = document.querySelector('[data-country-popup]');
    if (!this.popup) return;

    this.openBtn = document.querySelector('[data-country-switcher-open]');
    this.closeBtns = document.querySelectorAll('[data-country-popup-close]');
    this.overlay = document.querySelector('[data-country-popup-overlay]');
    this.dropdown = document.querySelector('[data-country-dropdown]');
    this.dropdownBtn = document.querySelector('[data-country-dropdown-btn]');
    this.dropdownMenu = document.querySelector('[data-country-dropdown-menu]');
    this.searchInput = document.querySelector('[data-country-search]');
    this.countryList = document.querySelector('[data-country-list]');
    this.countryOptions = document.querySelectorAll('[data-country-option]');
    this.countryInput = document.querySelector('[data-country-input]');
    this.form = document.getElementById('CountrySwitcherForm');
    this.submitBtn = document.querySelector('[data-country-submit-btn]');

    // Store initial country code
    this.initialCountryCode = this.countryInput ? this.countryInput.value : '';

    this.init();
  }

  init() {
    // Open popup
    if (this.openBtn) {
      this.openBtn.addEventListener('click', () => this.openPopup());
    }

    // Close popup
    this.closeBtns.forEach(btn => {
      btn.addEventListener('click', () => this.closePopup());
    });

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.closePopup());
    }

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.popup.hidden) {
        this.closePopup();
      }
    });

    // Dropdown toggle
    if (this.dropdownBtn) {
      this.dropdownBtn.addEventListener('click', () => this.toggleDropdown());
    }

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (this.dropdown && !this.dropdown.contains(e.target)) {
        this.closeDropdown();
      }
    });

    // Search functionality
    if (this.searchInput) {
      this.searchInput.addEventListener('input', (e) => this.handleSearch(e.target.value));
    }

    // Country option selection
    this.countryOptions.forEach(option => {
      option.addEventListener('click', () => this.selectCountry(option));
    });

    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }

    // Update dropdown position on window resize
    window.addEventListener('resize', () => {
      if (!this.dropdownMenu.hidden) {
        this.positionDropdown();
      }
    });
  }

  openPopup() {
    this.popup.hidden = false;
    document.body.classList.add('country-popup-open');
    
    // Reset submit button to disabled state when popup opens
    if (this.submitBtn) {
      this.submitBtn.disabled = true;
    }
    
    // Focus on search input when popup opens
    setTimeout(() => {
      if (this.searchInput) {
        this.searchInput.focus();
      }
    }, 100);
  }

  closePopup() {
    this.popup.hidden = true;
    document.body.classList.remove('country-popup-open');
    this.closeDropdown();
    
    // Reset search
    if (this.searchInput) {
      this.searchInput.value = '';
      this.handleSearch('');
    }

    // Reset to initial country when closing without submitting
    if (this.countryInput && this.initialCountryCode) {
      this.countryInput.value = this.initialCountryCode;
      
      // Reset dropdown display to initial country
      const initialOption = document.querySelector(`[data-country-option][data-country-code="${this.initialCountryCode}"]`);
      if (initialOption && this.dropdownBtn) {
        const dropdownCurrent = this.dropdownBtn.querySelector('.country-switcher-popup__dropdown-current span:last-child');
        if (dropdownCurrent) {
          dropdownCurrent.textContent = initialOption.querySelector('span:last-child').textContent;
        }

        const dropdownFlag = this.dropdownBtn.querySelector('.f-country-flags');
        if (dropdownFlag) {
          dropdownFlag.className = `f-country-flags f-country-flags--${this.initialCountryCode}`;
        }
      }

      // Update active state
      this.countryOptions.forEach(opt => {
        const code = opt.getAttribute('data-country-code');
        if (code === this.initialCountryCode) {
          opt.classList.add('country-switcher-popup__country-option--active');
        } else {
          opt.classList.remove('country-switcher-popup__country-option--active');
        }
      });
    }

    // Reset submit button
    if (this.submitBtn) {
      this.submitBtn.disabled = true;
    }
  }

  toggleDropdown() {
    const isExpanded = this.dropdownBtn.getAttribute('aria-expanded') === 'true';
    
    if (isExpanded) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown() {
    this.dropdownBtn.setAttribute('aria-expanded', 'true');
    this.dropdownMenu.hidden = false;
    
    // Position dropdown menu centered on screen
    this.positionDropdown();
    
    // Focus on search input when dropdown opens
    setTimeout(() => {
      if (this.searchInput) {
        this.searchInput.focus();
      }
    }, 100);
  }

  positionDropdown() {
    if (!this.dropdownMenu || !this.dropdownBtn) return;
    
    const btnRect = this.dropdownBtn.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const menuHeight = 500; // max-height from CSS
    
    // Calculate vertical position
    let top = btnRect.bottom + 8; // 8px gap below button
    
    // If dropdown would go off bottom of screen, position it above button
    if (top + menuHeight > viewportHeight) {
      top = btnRect.top - menuHeight - 8;
    }
    
    // Ensure it doesn't go off top of screen
    if (top < 8) {
      top = 8;
    }
    
    this.dropdownMenu.style.top = `${top}px`;
  }

  closeDropdown() {
    if (this.dropdownBtn && this.dropdownMenu) {
      this.dropdownBtn.setAttribute('aria-expanded', 'false');
      this.dropdownMenu.hidden = true;
    }
  }

  handleSearch(searchTerm) {
    const term = searchTerm.toLowerCase().trim();
    
    this.countryOptions.forEach(option => {
      const countryName = option.getAttribute('data-country-name').toLowerCase();
      const countryCode = option.getAttribute('data-country-code').toLowerCase();
      const matchesSearch = countryName.includes(term) || countryCode.includes(term);
      
      option.hidden = !matchesSearch;
    });
  }

  selectCountry(option) {
    const countryCode = option.getAttribute('data-country-code');
    const countryName = option.getAttribute('data-country-name');
    
    // Update hidden input
    if (this.countryInput) {
      this.countryInput.value = countryCode;
    }

    // Update dropdown button text
    const dropdownCurrent = this.dropdownBtn.querySelector('.country-switcher-popup__dropdown-current span:last-child');
    if (dropdownCurrent) {
      dropdownCurrent.textContent = option.querySelector('span:last-child').textContent;
    }

    // Update flag
    const dropdownFlag = this.dropdownBtn.querySelector('.f-country-flags');
    if (dropdownFlag) {
      dropdownFlag.className = `f-country-flags f-country-flags--${countryCode}`;
    }

    // Update active state
    this.countryOptions.forEach(opt => {
      opt.classList.remove('country-switcher-popup__country-option--active');
    });
    option.classList.add('country-switcher-popup__country-option--active');

    // Enable/disable submit button based on whether country changed
    this.updateSubmitButton(countryCode);

    // Close dropdown
    this.closeDropdown();
  }

  updateSubmitButton(selectedCountryCode) {
    if (!this.submitBtn) return;
    
    // Enable button only if selected country is different from initial country
    if (selectedCountryCode !== this.initialCountryCode) {
      this.submitBtn.disabled = false;
    } else {
      this.submitBtn.disabled = true;
    }
  }

  handleSubmit(e) {
    // Shopify will handle the form submission automatically
    // The page will reload with the new country selected
    console.log('Submitting country change:', this.countryInput.value);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CountrySwitcherPopup();
  });
} else {
  new CountrySwitcherPopup();
}

